
import { useEffect } from "react";
import * as services from "../services/inventoryService";
import { getInventorySettings } from "../../verve-inventory-settings/services/services";
import api from "../../../services/api";

export const useInventoryData = (user, activeTab, state) => {
  const {
    setInventoryState, setMRState, setReturnsState, setSalesState,
    setPurchaseReturnsState, setPurchases, setOpeningStocks, setDamagedItemsState, setProjectsLocal, setLoading,
    setCats, setSubCats, setUnits, setSuppliers, setBrands, setStores, setMasterItems
  } = state;

  const refreshAll = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const role = String(user?.role || "").toLowerCase();
      const canReadExecution = [
        "owner", "admin", "solar-admin", "sales-manager", "project-manager",
        "store-manager", "finance-manager", "field_officer", "telecaller",
      ].includes(role);

      const safeFetch = async (promise, name) => {
        try {
          const res = await promise;
          return res;
        } catch (err) {
          console.error(`Failed to fetch ${name}:`, err);
          return [];
        }
      };

      const [
        invData, mrData, retData, projData, salesData, prData, pData, osData,
        damagedItemsData, itemMasterRes, catRes, subCatRes, vendorRes, brandRes, unitRes, storeRes
      ] = await Promise.all([
        safeFetch(services.fetchInventory(), "inventory"),
        safeFetch(services.fetchMaterialRequests(activeTab), "requests"),
        safeFetch(services.fetchMaterialReturns(activeTab), "returns"),
        safeFetch(canReadExecution ? api.get('/projects') : Promise.resolve({ projects: [] }), "execution"),
        safeFetch(services.fetchSales(activeTab), "sales"),
        safeFetch(services.fetchPurchaseReturns(activeTab), "p-returns"),
        safeFetch(services.fetchPurchases(activeTab), "purchases"),
        safeFetch(services.fetchOpeningStocks(activeTab), "opening-stock"),
        safeFetch(canReadExecution ? api.get('/inventory/damaged-items') : Promise.resolve({ data: { data: [] } }), "damaged-items"),
        safeFetch(getInventorySettings({ settingType: "item", limit: 10000, status: "ACTIVE" }), "item-master"),
        safeFetch(getInventorySettings({ settingType: "category", limit: 10000, status: "ACTIVE" }), "categories"),
        safeFetch(getInventorySettings({ settingType: "subCategory", limit: 10000, status: "ACTIVE" }), "subCategories"),
        safeFetch(getInventorySettings({ settingType: "vendor", limit: 10000, status: "ACTIVE" }), "vendors"),
        safeFetch(getInventorySettings({ settingType: "brand", limit: 10000, status: "ACTIVE" }), "brands"),
        safeFetch(getInventorySettings({ settingType: "unit", limit: 10000, status: "ACTIVE" }), "units"),
        safeFetch(getInventorySettings({ settingType: "stores", limit: 10000, status: "ACTIVE" }), "stores")
      ]);

      const extInv = invData?.data?.data || invData?.data || invData || [];
      setInventoryState(Array.isArray(extInv) ? extInv : (extInv.items || []));

      const extMR = mrData?.data?.requests || mrData?.data?.data || mrData?.data || mrData || [];
      setMRState(Array.isArray(extMR) ? extMR : (extMR.requests || []));

      const extOS = osData?.data?.data || osData?.data || osData || [];
      setOpeningStocks(Array.isArray(extOS) ? extOS : (extOS.stocks || []));

      const extDI = damagedItemsData?.data?.data || damagedItemsData?.data || damagedItemsData || [];
      setDamagedItemsState(Array.isArray(extDI) ? extDI : []);

      const prj = projData?.data?.data || projData?.data || projData || [];
      setProjectsLocal(Array.isArray(prj) ? prj : (prj.projects || []));

      const extRet = retData?.data?.returns || retData?.data?.data || retData?.data || retData || [];
      setReturnsState(Array.isArray(extRet) ? extRet : (extRet.returns || []));

      const extSales = salesData?.data?.combined || salesData?.data?.data || salesData?.data || salesData || [];
      setSalesState(Array.isArray(extSales) ? extSales : (extSales.combined || []));
      const extractedPR = prData?.data?.returns || prData?.returns || prData?.data?.data || prData?.data || (Array.isArray(prData) ? prData : []);
      setPurchaseReturnsState(Array.isArray(extractedPR) ? extractedPR : (extractedPR?.returns || []));
      setPurchases(pData.data?.data || pData.data || (Array.isArray(pData) ? pData : []));
      setOpeningStocks(Array.isArray(osData) ? osData : (osData.data?.data || osData.data || []));

      const pList = projData.projects || projData.data?.projects || (Array.isArray(projData) ? projData : (Array.isArray(projData.data) ? projData.data : []));
      setProjectsLocal(pList);

      const extract = (res) => {
        const d = res?.data?.data || res?.data || res || [];
        if (Array.isArray(d)) return d;
        if (d && typeof d === 'object') {
          // Find first array value in object
          const arrVal = Object.values(d).find(val => Array.isArray(val));
          if (arrVal) return arrVal;
        }
        return [];
      };
      
      const masterItemsData = extract(itemMasterRes);
      setMasterItems(masterItemsData);
      
      const catsData = extract(catRes);
      const fetchedSubCats = extract(subCatRes);
      
      // We will derive missing cats and subcats from masterItems and purchases
      const pDataArr = pData.data?.data || pData.data || (Array.isArray(pData) ? pData : []);
      const allSources = [...masterItemsData, ...pDataArr];

      const uniqueCats = new Map(catsData.filter(c => c).map(c => [String(c.categoryName || c.name || c.title || "").toLowerCase().trim(), c]));
      allSources.forEach(item => {
        const catObj = item.category || item.item?.category;
        let catName = typeof catObj === 'object' ? (catObj?.categoryName || catObj?.name || catObj?.title || "") : (catObj || "");
        catName = catName || item.categoryName || item.item?.categoryName || "";
        catName = String(catName).trim();
        if (catName && !uniqueCats.has(catName.toLowerCase())) {
          uniqueCats.set(catName.toLowerCase(), { _id: catName, categoryName: catName });
        }
      });
      setCats(Array.from(uniqueCats.values()));

      const uniqueSubCats = new Map(fetchedSubCats.filter(sc => sc).map(sc => [String(sc.subCategoryName || sc.name || sc.title || "").toLowerCase().trim(), sc]));
      allSources.forEach(item => {
        const scObj = item.subCategory || item.item?.subCategory;
        let scName = typeof scObj === 'object' ? (scObj?.subCategoryName || scObj?.name || scObj?.title || "") : (scObj || "");
        scName = scName || item.subCategoryName || item.item?.subCategoryName || "";
        scName = String(scName).trim();
        
        const catObj = item.category || item.item?.category;
        let catName = typeof catObj === 'object' ? (catObj?.categoryName || catObj?.name || catObj?.title || "") : (catObj || "");
        catName = catName || item.categoryName || item.item?.categoryName || "";
        
        if (scName && !uniqueSubCats.has(scName.toLowerCase())) {
          uniqueSubCats.set(scName.toLowerCase(), { _id: scName, subCategoryName: scName, category: catName });
        }
      });
      setSubCats(Array.from(uniqueSubCats.values()));
      setSuppliers(extract(vendorRes));
      setBrands(extract(brandRes));
      setUnits(extract(unitRes));
      setStores(extract(storeRes));
    } catch (err) {
      console.error("Failed to load inventory data:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll(true);
  }, [activeTab, user?.role]);

  return { refreshAll };
};
